import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { PlusIcon } from '@heroicons/react/20/solid'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'
import _ from 'lodash'

// Images
import Badger from '../../assets/images/badger.svg'
import Lead from '../../assets/images/fetch.svg'
import Trackr from '../../assets/images/trackr.svg'

// Components
import { AddEventModal } from '../../components/AddEventModal'
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { DataTile } from '../../components/DataTile'
import { NeedsSetupBadge } from '../../components/NeedsSetupBadge'
import { LocationIconSolid } from '../../components/LocationIconSolid'
import { StateContainer } from '../../components/StateContainer'
import { WelcomeTile } from '../../components/WelcomeTile'

// Store
import { NavigationStoreContext } from '../../stores/NavigationStore'
import { UserStoreContext } from '../../stores/UserStore'

// Service
import { getEventsForOrganization, getEventsForUser } from '../../services/events.service'
import { getOrganizationKpis } from '../../services/organizations.service'

// Utils & Styles
import baseColors, {
  configureActiveBackgroundStyles,
  configureActiveTextStyle,
} from '../../utils/colors'
import { handlePagination, toast } from '../../utils/helpers'

dayjs.extend(customParseFormat)

/**
 * The event list an org user reads spans every organization they belong to, so it is
 * not nested under one (EXEC-1550). EE users hold no organization roles, so that list
 * would be empty for them — on an organization's dashboard they still read that
 * organization's events.
 * @param {boolean} isEEUser
 * @param {string} organizationId
 */
const getEventsUrl = (isEEUser, organizationId) =>
  isEEUser ? `/organizations/${organizationId}/events/` : '/my-events/'

// Shown when a row arrives without the organization it's reached through. Every row
// the API returns names one, so this is a malformed row rather than a state a user
// can get themselves into — but it beats routing to `/organization/undefined/...`
// or asking the API for an event under an undefined organization.
const MISSING_ORGANIZATION_ERROR =
  'This event is missing its company information. Please refresh and try again.'

/**
 * Whether a row's company has not finished setting up for the event.
 *
 * Organizer rows carry no exhibitor record, so they are never inactive — the event's own
 * date-driven status is the only one that applies to them.
 * @param {object} row
 */
const isRowInactive = (row) => !!row.eventExhibitor && !row.eventExhibitor.hasCompletedOnboarding

/**
 * Routes for a dashboard row, which may belong to a different organization than
 * the one currently active in the navigation store.
 *
 * Organization-scoped rows (what EE users get) carry no organization of their own, so
 * they fall back to the one being viewed. `organizationId` and `path` are null when
 * neither is available, so callers can bail before building a URL around `undefined`.
 * @param {object} row
 * @param {object} fallback the active entity, as `{ type, organizationId }`
 */
const getRowNavigation = (row, fallback = {}) => {
  const organizationId = row.organization?.id ?? fallback.organizationId ?? null

  let rowType
  if (row.organization?.type) {
    rowType = row.organization.type === 'Exhibitor' ? 'exhibitor' : 'organization'
  } else {
    rowType = fallback.type === 'exhibitor' ? 'exhibitor' : 'organization'
  }

  return {
    type: rowType,
    organizationId,
    path: organizationId ? `/${rowType}/${organizationId}/event/${row.id}/home` : null,
  }
}

/**
 *
 * OrganizationDashboard
 *
 */
const OrganizationDashboard = observer(() => {
  // Context
  const { organizationId, setEventId, type, updateActiveEntity } =
    useContext(NavigationStoreContext)
  const { isEEUser, user } = useContext(UserStoreContext)

  // The dashboard is no longer scoped to an organization, so event creation gets the
  // organizers this user could create under from their own roles. With exactly one we
  // pass it straight through; with several the modal asks which.
  const organizerOptions = _.map(
    _.filter(user?.organizationRoles ?? [], (r) => r.organization?.type === 'Organizer'),
    (r) => ({ id: r.organization.id, label: r.organization.name }),
  )
  const canAddEvent = !!organizationId || organizerOptions.length > 0
  const addEventOrganizationId =
    organizationId ?? (organizerOptions.length === 1 ? organizerOptions[0].id : null)
  const eventsUrl = getEventsUrl(isEEUser, organizationId)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const onboardingEventId = searchParams.get('onboarding')

  // State
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [kpis, setKpis] = useState(null)
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [events, setEvents] = useState([])
  const [showEventModal, setShowEventModal] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [pages, setPages] = useState(null)

  /**
   * Manually add the `group` className to each event row
   * so that group styles apply to the custom event name cell.
   */
  useEffect(() => {
    if (events) {
      // Add a very short timeout to ensure the table is displayed
      // before we modify the row styling.
      setTimeout(() => {
        const eventRows = document.querySelectorAll('.rdt_TableRow')
        eventRows.forEach((row) => {
          // Add className for `group`
          if (!row.classList.contains('group')) row.classList.add('group')
        })
      }, [300])
    }
  }, [events])

  const handleErrors = (m) => toast(m, 'error')

  /**
   * Gets the updated list of events; updates pagination.
   * @param {string} url
   * @param {boolean} skipLoading
   * @returns list of results
   */
  const getUpdatedEventList = async (url) => {
    // `setSuccess` defaults to a no-op in both helpers, so it is left off here.
    const response = isEEUser
      ? await getEventsForOrganization(url, organizationId, handleErrors, setLoadingEvents)
      : await getEventsForUser(url, handleErrors, setLoadingEvents)

    if (response) {
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })

      setEvents(response.results)
      return response.results
    }

    return []
  }

  /**
   * Opens a dashboard row's event, moving the active entity to that row's
   * organization first — it isn't necessarily the one the user is viewing now.
   *
   * Every row opens, including one whose company has not finished setting up; the event
   * home raises the setup flow itself on arrival (EXEC-1596).
   * @param {object} row
   */
  const navigateToRow = (row) => {
    const {
      type: rowType,
      organizationId: rowOrganizationId,
      path,
    } = getRowNavigation(row, {
      type,
      organizationId,
    })

    if (!path) {
      handleErrors(MISSING_ORGANIZATION_ERROR)
      return
    }

    updateActiveEntity(rowType, rowOrganizationId)
    setEventId(row.id)
    navigate(path)
  }

  useEffect(() => {
    const loadData = async () => {
      // The portal dashboard is user-scoped and has no organization in the path, so
      // there are no organization KPIs to fetch (EXEC-1550).
      if (!organizationId) {
        setLoading(false)
        return
      }

      const result = await getOrganizationKpis(
        organizationId,
        setError,
        () => {},
        () => {},
      )
      setKpis(result)
      setLoading(false)
    }

    loadData()
  }, [organizationId])

  /**
   * When the row count or the list's source changes, get the updated event list.
   *
   * For an org user the source is fixed: the list spans every organization they
   * belong to (EXEC-1550), so it no longer reloads when the active organization
   * changes. For an EE user it is that organization's list, so it still does.
   */
  useEffect(() => {
    setCurrentPage(1)
    getUpdatedEventList(`${eventsUrl}?order_by=-starts_at&limit=${perPage}`)
  }, [eventsUrl, perPage])

  /**
   * `?onboarding=<event id>` is the link in the licence email. Setup itself lives on the
   * event home now (EXEC-1596), so the dashboard's job is only to find the row — it is
   * what says which company the event is reached through — and open it. The event home
   * raises the setup flow from there.
   */
  useEffect(() => {
    if (onboardingEventId && events) {
      const match = _.find(events, { id: onboardingEventId })

      if (match) {
        navigateToRow(match)
      }
    }
  }, [onboardingEventId, events])

  /**
   * Renders the `Status` cell in the DataTable
   * - Displays a pill label based on event `status`
   * @param {object} row
   */
  const renderStatusCell = (row) => (
    <div
      className={mergeClassNames(
        'shrink-0 rounded-full px-2.5 py-0.5',
        configureActiveBackgroundStyles(row),
      )}
    >
      <span
        className={mergeClassNames('text-xs font-bold', configureActiveTextStyle(row))}
        data-tag="allowRowEvents"
      >
        {row.status}
      </span>
    </div>
  )

  // An org user's list spans organizations, so each row names the company it's reached
  // through and the role they hold there (EXEC-1550). An EE user's list is a single
  // organization's, where the column would just repeat one name down the page.
  const companyColumn = {
    id: 'company',
    cell: (row) => (
      <div className="flex flex-col group-hover:text-white" data-tag="allowRowEvents">
        <span className="truncate text-sm font-medium" data-tag="allowRowEvents">
          {row.organization?.name}
        </span>
        <span className="text-xs text-gray-500 group-hover:text-white" data-tag="allowRowEvents">
          {row.role}
        </span>
      </div>
    ),
    grow: 1,
    name: 'Company',
    selector: (row) => row.organization?.name,
    minWidth: '180px',
  }
  const companyColumns = isEEUser ? [] : [companyColumn]

  const columns = [
    {
      id: 'name',
      grow: 2,
      name: 'Event Name',
      selector: (row) => row.name,
      cell: (row) => (
        <>
          <div className="flex w-full flex-col space-y-1">
            <div className="flex w-full flex-row place-items-center space-x-2">
              <span className="truncate text-sm font-medium text-black-500 group-hover:text-teal">
                {row.name}
              </span>

              {renderStatusCell(row)}

              {/* The company's own status, which is independent of the event's dates */}
              {isRowInactive(row) && <NeedsSetupBadge />}
            </div>

            <div className="flex w-full flex-row place-items-center text-gray-500 group-hover:text-white">
              {row.location && (
                <div className="flex flex-row place-items-center space-x-2">
                  <LocationIconSolid className="fill-gray-500 group-hover:fill-white" />
                  <span>{row.location}</span>
                </div>
              )}
            </div>
          </div>
          {/*
            This element is necessary for the onRowClicked handler to function
            without having to add the `data-tag` to the innermost elements in this custom cell
            more info here: https://react-data-table-component.netlify.app/?path=/docs/api-props--page#general-properties
          */}
          <div
            style={{ height: '100%', position: 'absolute', width: '100%' }}
            data-tag="allowRowEvents"
          ></div>
        </>
      ),
      sortBy: 'name',
      sortable: true,
      minWidth: '300px',
    },
    ...companyColumns,
    {
      id: 'dates',
      cell: (row) => (
        <div className="group-hover:text-white">
          <span data-tag="allowRowEvents">{row.dateRange}</span>
        </div>
      ),
      grow: 1,
      name: 'Dates',
      selector: (row) => row.dateRange,
      sortable: true,
      sortBy: 'starts_at',
      minWidth: '180px',
    },
  ]

  if (type === 'organizer-organization') {
    columns.push({
      id: 'stack',
      center: true,
      width: '100px',
      name: 'Stack',
      cell: (row) => {
        const stack = []

        if (row.enableLeadRetrieval)
          stack.push(
            <img
              alt="lead retrieval"
              className="h-6"
              key={`event:${row.id}:lead`}
              src={Lead}
              data-tag="allowRowEvents"
            />,
          )
        if (row.enableBadging)
          stack.push(
            <img
              alt="badging"
              className="h-6"
              key={`event:${row.id}:badging`}
              src={Badger}
              data-tag="allowRowEvents"
            />,
          )
        if (import.meta.env.VITE_TRACKR_ENABLED === 'true' && row.enableTrackr)
          stack.push(
            <img alt="tracker" className="h-7" key={`event:${row.id}:tracker`} src={Trackr} />,
          )

        return (
          <div className="flex w-full justify-center space-x-2" data-tag="allowRowEvents">
            {stack}
          </div>
        )
      },
    })
  }

  return (
    <div className="h-full w-full">
      <StateContainer error={error} loading={loading}>
        <div className="relative flex h-full w-full flex-col space-y-4 overflow-y-auto p-3">
          <span className="ml-5 text-2xl font-semibold">Dashboard</span>
          <WelcomeTile type={type} />

          {/*
            These tiles stay scoped to the active organization while the table below
            spans every organization the user belongs to, so Total Events is derived
            from the same org KPIs as the other two rather than from the table's row
            count — otherwise it would silently count another company's events.
          */}
          {kpis && type === 'organization' && (
            <div className="flex w-full flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0">
              <DataTile
                color="purple"
                label="Total Events"
                value={kpis.activeEventCount + kpis.closedEventCount}
              />

              <DataTile label="Active Events" value={kpis.activeEventCount} />

              <DataTile color="red" label="Closed Events" value={kpis.closedEventCount} />
            </div>
          )}

          <div className="h-full w-full">
            <div className="mb-4 flex w-full flex-row place-items-center justify-between">
              <span className="ml-5 text-2xl font-semibold">Events</span>

              {/* Org users create their own events since EXEC-1543 */}
              {canAddEvent && (
                <Button
                  background="bg-purple border-purple hover:bg-purple-600"
                  icon={<PlusIcon className="h-5 sm:h-6" />}
                  label="Add Event"
                  onClick={() => setShowEventModal(true)}
                />
              )}
            </div>

            <DataTable
              columns={columns}
              // FIXME: remove custom loading so that its not confusing on first load
              customLoading={false}
              customStyles={{
                responsiveWrapper: {
                  style: {
                    border: `1px solid ${baseColors.gray.light}`,
                    borderRadius: '8px',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                  },
                },
                head: {
                  style: {
                    height: '40px',
                  },
                },
                headRow: {
                  style: {
                    backgroundColor: baseColors.gray[100],
                    textTransform: 'uppercase',
                    minHeight: '40px',
                    borderBottomColor: baseColors.gray.light,
                  },
                },
                pagination: {
                  style: {
                    color: baseColors.gray[900],
                    fontSize: '12px',
                    minHeight: '30px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontWeight: '600',
                  },
                  pageButtonsStyle: {
                    borderRadius: '50%',
                    height: '32px',
                    width: '32px',
                    padding: '4px',
                    margin: 'px',
                    cursor: 'pointer',
                    transition: '0.4s',
                    outline: 'none',
                    color: 'black',
                    fill: 'black',
                    backgroundColor: 'transparent',
                    '&:disabled': {
                      cursor: 'unset',
                      color: 'black',
                      opacity: 0.25,
                      fill: 'black',
                    },
                    '&:hover:not(:disabled)': {
                      backgroundColor: baseColors.purple.DEFAULT,
                      fill: 'white',
                    },
                    '&:focus': {
                      outline: 'none',
                      backgroundColor: baseColors.purple.DEFAULT,
                      fill: 'white',
                    },
                  },
                },
                rows: {
                  style: {
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: baseColors.gray[600],
                    height: '70px',
                    '&:not(:last-of-type)': {
                      borderBottomColor: baseColors.gray.light,
                    },
                    '&:hover': {
                      backgroundColor: baseColors.gray[900],
                      color: baseColors.white,
                      cursor: 'pointer',
                    },
                  },
                },
              }}
              data={events}
              defaultSortFieldId="dates"
              defaultSortAsc={false}
              onRowClicked={(row) => navigateToRow(row)}
              onChangePage={(page) =>
                handlePagination(
                  page,
                  currentPage,
                  perPage,
                  totalRows,
                  pages,
                  setCurrentPage,
                  getUpdatedEventList,
                  `${eventsUrl}?order_by=-starts_at&limit=`,
                )
              }
              onChangeRowsPerPage={async (currentRowsPerPage) => setPerPage(currentRowsPerPage)}
              onSort={(column, direction) => {
                const d = direction === 'asc' ? '' : '-'
                const url = `${eventsUrl}?order_by=${d}${column.sortBy}&limit=${perPage}`
                getUpdatedEventList(url)
              }}
              pagination
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[10, 15, 20, 30, 50]}
              paginationTotalRows={totalRows}
              paginationServer
              progressPending={loadingEvents}
              sortServer
              sortingEnabled
            />
          </div>
        </div>
      </StateContainer>

      {showEventModal && (
        <AddEventModal
          loadData={() => getUpdatedEventList(`${eventsUrl}?order_by=-starts_at&limit=${perPage}`)}
          organization={addEventOrganizationId}
          organizations={organizerOptions}
          setShowEventModal={setShowEventModal}
        />
      )}
    </div>
  )
})

export default OrganizationDashboard
